import { produce } from 'immer'
import moment from 'moment/moment'
import { create } from 'zustand'

import { FilterOption, FilterPreset, FilterStateV2 } from 'types/project'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatDateToUTCMidnight = (date: moment.Moment): moment.Moment =>
  date.utc().set({ hour: 0, minute: 0, second: 0, millisecond: 0 })

const formatDateToUTCEndOfDay = (date: moment.Moment): moment.Moment =>
  date.utc().set({ hour: 23, minute: 59, second: 0, millisecond: 0 })

// ===========================================================================
// Filter Data Store — core filter state (filters array, loading flags)
// ===========================================================================

export type FilterState = {
  filters: FilterStateV2[]
  setFilters: (filters: FilterStateV2[]) => void
  updateFilters: (comparativeIndex: number, value: FilterStateV2) => void
  setFilterValues: (
    comparativeIndex: number,
    values: FilterStateV2['filters'],
  ) => void

  hasLoadedFilters: boolean
  setHasLoadedFilters: (v: boolean) => void

  selectedCountry: string[]
  setSelectedCountry: (values: string[]) => void

  resetFilterState: () => void
}

export const initialFilterState = {
  filters: [
    {
      filters: [],
      searchTerms: [],
      searchCriteria: {},
      dateRange: {
        startDate: formatDateToUTCMidnight(moment().subtract(1, 'year')).unix(),
        endDate: formatDateToUTCEndOfDay(moment()).unix(),
      },
    },
  ] as FilterStateV2[],
  hasLoadedFilters: false,
  selectedCountry: [] as string[],
}

export const useFilterStore = create<FilterState>()((set) => ({
  ...initialFilterState,

  setFilters: (filters) =>
    set(
      produce((s: FilterState) => {
        s.filters = filters
      }),
    ),

  updateFilters: (comparativeIndex, value) =>
    set(
      produce((s: FilterState) => {
        if (s.filters[comparativeIndex] != null)
          s.filters[comparativeIndex] = value
      }),
    ),

  setFilterValues: (comparativeIndex, values) =>
    set(
      produce((s: FilterState) => {
        const entry = s.filters[comparativeIndex]
        if (entry) entry.filters = values
      }),
    ),

  setHasLoadedFilters: (v) => set({ hasLoadedFilters: v }),

  setSelectedCountry: (values) => set({ selectedCountry: values }),

  resetFilterState: () => set(initialFilterState),
}))

// ===========================================================================
// Filter UI Store — UI-specific filter state (filter list, opened options)
// ===========================================================================

export type FilterUIState = {
  filterList: Array<Array<FilterOption>>
  setFilterList: (values: Array<Array<FilterOption>>) => void
  setFilterListByIndex: (
    comparativeIndex: number,
    values: FilterOption[],
  ) => void
  resetFilterListByIndex: (comparativeIndex: number) => void

  defaultFilterList: Array<FilterOption>
  setDefaultFilterList: (values: Array<FilterOption>) => void

  openedOptions: string[][]
  setOpenedOptions: (values: string[][]) => void
  updateOpenedOptions: (comparativeIndex: number, value: string) => void
  clearOpenedOptions: (comparativeIndex: number) => void

  selectedOptionField: string[]
  setSelectedOptionField: (values: string[]) => void
  updateSelectedOptionField: (comparativeIndex: number, value: string) => void

  filterPresets: FilterPreset[]
  setFilterPresets: (filterPresets: FilterPreset[]) => void

  relativeDateFilterIdx: number[]
  setRelativeDateFilterIdx: (relativeDateFilterIdx: number[]) => void
  updateRelativeDateFilterIdx: (
    comparativeIndex: number,
    relativeDateFilterIdx: number,
  ) => void

  isAlignFilters: boolean
  setIsAlignFilters: (isAlignFilters: boolean) => void

  resetFilterUI: () => void
}

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

export const initialFilterUIState = {
  filterList: [[]] as Array<Array<FilterOption>>,
  defaultFilterList: [] as Array<FilterOption>,
  openedOptions: [[]] as string[][],
  selectedOptionField: [''] as string[],
  filterPresets: [] as FilterPreset[],
  relativeDateFilterIdx: [-1] as number[],
  isAlignFilters: false,
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useFilterUIStore = create<FilterUIState>()((set) => ({
  ...initialFilterUIState,

  setFilterList: (values) =>
    set(
      produce((state: FilterUIState) => {
        state.filterList = values
      }),
    ),

  setFilterListByIndex: (comparativeIndex, values) =>
    set(
      produce((state: FilterUIState) => {
        const temp = state.filterList[comparativeIndex]
        if (
          temp &&
          temp.length > 0 &&
          state.openedOptions[comparativeIndex]?.length
        ) {
          temp.forEach((el, index) => {
            const v = values[index]
            if (v) temp[index] = { ...el, ...v }
          })
          state.filterList[comparativeIndex] = temp
        } else {
          state.filterList[comparativeIndex] = values
        }
      }),
    ),

  resetFilterListByIndex: (comparativeIndex) =>
    set(
      produce((state: FilterUIState) => {
        state.filterList[comparativeIndex] = []
      }),
    ),

  setDefaultFilterList: (values) =>
    set(
      produce((state: FilterUIState) => {
        state.defaultFilterList = values
      }),
    ),

  setOpenedOptions: (values) =>
    set(
      produce((state: FilterUIState) => {
        state.openedOptions = values
      }),
    ),

  updateOpenedOptions: (comparativeIndex, value) =>
    set(
      produce((state: FilterUIState) => {
        const opts = state.openedOptions[comparativeIndex]
        if (opts && !opts.includes(value)) {
          opts.push(value)
        }
      }),
    ),

  clearOpenedOptions: (comparativeIndex) =>
    set(
      produce((state: FilterUIState) => {
        state.openedOptions[comparativeIndex] = []
      }),
    ),

  setSelectedOptionField: (values) =>
    set(
      produce((state: FilterUIState) => {
        state.selectedOptionField = values
      }),
    ),

  updateSelectedOptionField: (comparativeIndex, value) =>
    set(
      produce((state: FilterUIState) => {
        state.selectedOptionField[comparativeIndex] = value
      }),
    ),

  setFilterPresets: (filterPresets) =>
    set(
      produce((state: FilterUIState) => {
        state.filterPresets = filterPresets
      }),
    ),

  setRelativeDateFilterIdx: (relativeDateFilterIdx) =>
    set(
      produce((state: FilterUIState) => {
        state.relativeDateFilterIdx = relativeDateFilterIdx
      }),
    ),

  updateRelativeDateFilterIdx: (comparativeIndex, relativeDateFilterIdx) =>
    set(
      produce((state: FilterUIState) => {
        state.relativeDateFilterIdx[comparativeIndex] = relativeDateFilterIdx
      }),
    ),

  setIsAlignFilters: (isAlignFilters) =>
    set(
      produce((state: FilterUIState) => {
        state.isAlignFilters = isAlignFilters
      }),
    ),

  resetFilterUI: () => set(initialFilterUIState),
}))
